import React, { useState, useEffect } from "react";
import { X, Loader2, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";

const API_URL = process.env.REACT_APP_API_URL;

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
  });

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle");

  // Fetch courses from backend
  useEffect(() => {
    if (isOpen) {
      const fetchCourses = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${API_URL}/courses/get-all`);
          if (response.data.success) {
            setCourses(response.data.data);
          }
        } catch (err) {
          console.error("Error fetching courses:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchCourses();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const response = await axios.post(`${API_URL}/contact/create`, formData);

      if (response.data.success) {
        setStatus("success");

        Swal.fire({
          icon: "success",
          title: "Enquiry Sent Successfully!",
          text: "We will get back to you soon",
          confirmButtonText: "OK",
          confirmButtonColor: "#DA3335",
          customClass: {
            popup: "rounded-2xl",
            confirmButton: "rounded-lg px-6 py-2",
          },
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          course: "",
          centre: "",
          message: "",
        });

        setTimeout(() => {
          setStatus("idle");
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setStatus("error");

      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          err.response?.data?.message ||
          "Failed to send Enquiry. Please try again.",
        confirmButtonText: "Try Again",
        confirmButtonColor: "#DA3335",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-lg px-6 py-2",
        },
      });

      setTimeout(() => setStatus("idle"), 2000);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      course: "",
      message: "",
    });
    setStatus("idle");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-2 bg-white">
          <div className="lg:p-2 p-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-2 leading-tight heading">
              Contact Our Support Team
            </h1>
            <p className="text-center text-gray-600 mb-8 sub-heading">
              Fill out the form below and we'll get back to you soon
            </p>
          </div>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 sm:p-8 md:py-4 md:px-10 sub-heading">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-yellow-300 focus:ring-2 focus:ring-yellow-100 transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-yellow-300 focus:ring-2 focus:ring-yellow-100 transition-all"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-yellow-300 focus:ring-2 focus:ring-yellow-100 transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Course *
                </label>
                <select
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white focus:outline-none focus:border-yellow-300 focus:ring-2 focus:ring-yellow-100 transition-all"
                  required
                  disabled={loading}
                >
                  <option value="">
                    {loading ? "Loading courses..." : "Choose a Course"}
                  </option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Type Your Message"
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl resize-none focus:outline-none focus:border-yellow-300 focus:ring-2 focus:ring-yellow-100 transition-all"
                required
              />
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              className={`w-full flex items-center justify-center gap-2 font-bold rounded-xl py-4 transition-all overflow-hidden text-white  sticky bottom-0 mb-1
          ${
            status === "success"
              ? "bg-green-500"
              : status === "error"
              ? "bg-red-500"
              : "bg-gradient-to-r from-[#DA3335] to-[#FF6B6B] hover:opacity-90"
          }`}
            >
              {status === "loading" && (
                <Loader2 className="animate-spin h-5 w-5 text-white" />
              )}
              {status === "success" && (
                <CheckCircle className="h-5 w-5 text-white animate-bounce" />
              )}
              {status === "error" && (
                <XCircle className="h-5 w-5 text-white animate-pulse" />
              )}
              <span>
                {status === "loading"
                  ? "Sending..."
                  : status === "success"
                  ? "Sent!"
                  : status === "error"
                  ? "Failed!"
                  : "Send Your Message"}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
